// Platform-agnostic palette for festival "zones" (stage / media office /
// crew catering / festival boundary). This is the single source of truth
// for zone colours + fill/border weights shared across BOTH the web app
// (apps/web, mapbox-gl) and the mobile crew app (apps/mobile, @rnmapbox).
//
// Each platform builds its own layer paint from these raw values:
//   - web:    apps/web/src/lib/zoneStyle.ts
//   - mobile: apps/mobile/app/(tabs)/tracking.tsx
//
// Keep this free of any Mapbox / React Native imports so both can consume it.
//
// Design language: a calm, muted palette. Fills stay low-opacity so a busy
// basemap stays readable; the colour lives mostly in the crisp border and
// the label. The festival boundary is a lightly-filled dashed neutral frame
// so it reads as "the outer edge" without competing with the real zones.

export type ZoneAreaType = "stage_zone" | "media_office" | "crew_catering" | "festival_boundary";

export interface ZonePalette {
  /** Primary hue for border + label accent. */
  color: string;
  /** Resting fill opacity. */
  fillOpacity: number;
  /** Slightly stronger fill while the polygon is active/selected. */
  activeFillOpacity: number;
  /** Border thickness in px. */
  lineWidth: number;
  /** Dash pattern for the border; omit for a solid line. */
  lineDash?: number[];
}

const COMMON = { fillOpacity: 0.16, activeFillOpacity: 0.24, lineWidth: 2 };

export const ZONE_PALETTE: Record<ZoneAreaType, ZonePalette> = {
  stage_zone: { color: "#2FA37C", ...COMMON }, // soft green
  media_office: { color: "#6366F1", ...COMMON }, // soft indigo
  crew_catering: { color: "#E0912F", ...COMMON }, // soft amber
  festival_boundary: {
    color: "#94A3B8", // neutral slate
    ...COMMON, // same grey fill weight as the real zones…
    lineWidth: 2.5,
    lineDash: [1.6, 1.6], // …but a dashed outline marks it as the outer edge
  },
};

/**
 * Resolve a zone palette from a (possibly unknown) area_type string,
 * falling back to the stage look so an unexpected value never renders
 * an invisible or crashing layer.
 */
export function getZonePalette(areaType: string | null | undefined): ZonePalette {
  if (areaType && areaType in ZONE_PALETTE) {
    return ZONE_PALETTE[areaType as ZoneAreaType];
  }
  return ZONE_PALETTE.stage_zone;
}

// Platform-agnostic look for the zone label "pill" and borders, shared so
// web and mobile can't drift. Only the raw colours/weights live here — the
// pill *geometry* (corner radius, padding, image size) stays platform-
// specific because a rasterised mapbox-gl image and an RN <View> are sized
// differently. Web builds a canvas pill image from these; mobile builds a
// StyleSheet. See apps/web/src/lib/zoneStyle.ts (ZONE_PILL) and
// apps/mobile/app/(tabs)/tracking.tsx.
export const ZONE_LABEL = {
  chipColor: "#0F172A", // dark chip behind the label text
  chipOpacity: 0.82,
  borderWidth: 2,
  textColor: "#F8FAFC",
  textSize: 12,
  lineOpacity: 0.9, // zone border opacity, shared by both platforms
} as const;

/** Convert a "#RRGGBB" hex + alpha (0–1) to an "rgba(r, g, b, a)" string. */
export function withAlpha(hex: string, alpha: number): string {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
